import './StaffSettingsSection.css'

import { useEffect, useMemo, useState } from 'react'
import { useAtomValue, useSetAtom } from 'jotai'

import { staff } from '../../../store'

import { Button } from '../../../components/Button'
import { DataTable } from '../../../components/DataTable'
import { EmptyState } from '../../../components/EmptyState'
import { PageHeader } from '../../../components/PageHeader'
import { SelectField } from '../../../components/SelectField'
import { Tag } from '../../../components/Tag'
import { TextField } from '../../../components/TextField'

const knownPermissions = Object.values(staff.Permission)

export function StaffSettingsStaffView() {
  const session = useAtomValue(staff.staffSessionAtom)
  const members = useAtomValue(staff.staffAdminStaffAtom)
  const roles = useAtomValue(staff.staffAdminRolesAtom)
  const isLoading = useAtomValue(staff.staffAdminIsLoadingAtom)
  const error = useAtomValue(staff.staffAdminErrorAtom)
  const actionLoading = useAtomValue(staff.staffActionIsLoadingAtom)

  const fetchAdmin = useSetAtom(staff.fetchStaffAdminAtom)
  const createMember = useSetAtom(staff.createStaffMemberAtom)
  const updateRole = useSetAtom(staff.updateStaffRoleAtom)

  const canRead = staff.hasStaffPermission(session, staff.Permission.SettingsStaffRead)
  const canWrite = staff.hasStaffPermission(session, staff.Permission.SettingsStaffWrite)

  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [password, setPassword] = useState('')
  const [roleId, setRoleId] = useState('')
  const [selectedRoleId, setSelectedRoleId] = useState('')
  const [draftRoleName, setDraftRoleName] = useState('')
  const [draftPermissions, setDraftPermissions] = useState<string[]>([])
  const [formError, setFormError] = useState<string | null>(null)
  const [roleError, setRoleError] = useState<string | null>(null)

  useEffect(() => {
    if (canRead) fetchAdmin()
  }, [canRead, fetchAdmin])

  useEffect(() => {
    if (!roleId && roles[0]) setRoleId(roles[0].id)
  }, [roles, roleId])

  useEffect(() => {
    if (!selectedRoleId && roles[0]) setSelectedRoleId(roles[0].id)
  }, [roles, selectedRoleId])

  useEffect(() => {
    const role = roles.find((item) => item.id === selectedRoleId)
    setDraftRoleName(role?.name ?? '')
    setDraftPermissions(role?.permissions ?? [])
    setRoleError(null)
  }, [roles, selectedRoleId])

  const roleItems = roles.map((role) => ({ value: role.id, text: role.name }))

  const columns = useMemo(
    () => [
      {
        id: 'email',
        header: 'Staff',
        render: (member: staff.StaffAdminMember) => (
          <div className="staff-settings-section__primary">
            <strong>{member.email}</strong>
            <span>{member.name}</span>
          </div>
        ),
      },
      {
        id: 'role',
        header: 'Role',
        render: (member: staff.StaffAdminMember) => member.roleName || member.roleId,
      },
      {
        id: 'status',
        header: 'Status',
        render: (member: staff.StaffAdminMember) => (
          <Tag color={member.isActive ? 'teal' : 'red'}>
            {member.isActive ? 'active' : 'inactive'}
          </Tag>
        ),
      },
      {
        id: 'created',
        header: 'Created',
        render: (member: staff.StaffAdminMember) => formatDate(member.createdAt),
      },
    ],
    [],
  )

  const handleCreate = async () => {
    setFormError(null)
    if (!email || !name || !password || !roleId) {
      setFormError('All fields are required.')
      return
    }
    const result = await createMember({ email, name, password, roleId })
    if (result.success) {
      setEmail('')
      setName('')
      setPassword('')
    } else {
      setFormError('Could not create staff member.')
    }
  }

  const handleSaveRole = async () => {
    if (!selectedRoleId) return
    setRoleError(null)
    const trimmedName = draftRoleName.trim()
    if (!trimmedName) {
      setRoleError('Role name is required.')
      return
    }
    const result = await updateRole({
      roleId: selectedRoleId,
      name: trimmedName,
      permissions: draftPermissions,
    })
    if (!result.success) {
      setRoleError(
        result.error === 'nameAlreadyInUse'
          ? 'That role name is already in use.'
          : 'Could not save role.',
      )
    }
  }

  const togglePermission = (permission: string) => {
    setDraftPermissions((current) =>
      current.includes(permission)
        ? current.filter((item) => item !== permission)
        : [...current, permission],
    )
  }

  if (!canRead) {
    return (
      <EmptyState
        title="No staff admin access"
        description="Your role does not include settings.staff.read."
      />
    )
  }

  return (
    <div className="staff-settings-section">
      <PageHeader
        title="Staff & roles"
        subtitle="Operator accounts and permission roles."
        actions={
          <Button
            kind="secondary"
            text={isLoading ? 'Loading…' : 'Refresh'}
            onClick={() => fetchAdmin()}
            disabled={isLoading}
          />
        }
      />

      {error && (
        <EmptyState
          title="Failed to load staff admin"
          description={error === 'forbidden' ? 'Forbidden.' : 'Request failed.'}
        />
      )}

      {!error && (
        <>
          <section className="staff-settings-section__panel">
            <h2 className="staff-settings-section__heading">Operators</h2>
            <DataTable
              columns={columns}
              rows={members}
              getRowKey={(member) => member.id}
              emptyMessage="No staff members"
            />
          </section>

          {canWrite && (
            <section className="staff-settings-section__panel">
              <h2 className="staff-settings-section__heading">Create staff member</h2>
              <div className="staff-settings-section__form">
                <TextField label="Email" value={email} onChange={setEmail} />
                <TextField label="Name" value={name} onChange={setName} />
                <TextField
                  label="Temporary password"
                  kind="password"
                  value={password}
                  onChange={setPassword}
                />
                <SelectField label="Role" items={roleItems} value={roleId} onChange={setRoleId} />
                {formError && <p className="staff-settings-section__error">{formError}</p>}
                <Button
                  kind="primary"
                  text={actionLoading ? 'Creating…' : 'Create'}
                  disabled={actionLoading}
                  onClick={handleCreate}
                />
              </div>
            </section>
          )}

          {canWrite && roles.length > 0 && (
            <section className="staff-settings-section__panel">
              <h2 className="staff-settings-section__heading">Edit role</h2>
              <div className="staff-settings-section__form">
                <SelectField
                  label="Role"
                  items={roleItems}
                  value={selectedRoleId}
                  onChange={setSelectedRoleId}
                />
                <TextField
                  label="Role name"
                  value={draftRoleName}
                  onChange={setDraftRoleName}
                  placeholder="e.g. Moderator"
                />
                <div className="staff-settings-section__permissions">
                  {knownPermissions.map((permission) => (
                    <label key={permission} className="staff-settings-section__permission">
                      <input
                        type="checkbox"
                        checked={draftPermissions.includes(permission)}
                        onChange={() => togglePermission(permission)}
                      />
                      <span>{permission}</span>
                    </label>
                  ))}
                </div>
                {roleError && <p className="staff-settings-section__error">{roleError}</p>}
                <Button
                  kind="primary"
                  text={actionLoading ? 'Saving…' : 'Save role'}
                  disabled={actionLoading}
                  onClick={handleSaveRole}
                />
              </div>
            </section>
          )}
        </>
      )}
    </div>
  )
}

function formatDate(value: string) {
  try {
    return new Date(value).toLocaleString()
  } catch {
    return value
  }
}
